import { call, put, select, takeEvery } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut,
  ThhHundProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  Produktlinie,
  THH_HUND_UEBERSICHT_TITLE,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ThhTier } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { ThhHundActions } from '../thhHundActions';
import contentData from '../../data/content-default.json';
import { AnzahlTiereEnum, Versicherungsnehmer } from '../reducers/versicherungsnehmer.reducer';

export function* watchInDashboardUebernehmenSaga() {
  yield takeEvery(
    ThhHundActions.ABSPRUNG_INS_DASHBOARD,
    safeSaga(generateDashboardData, App.THH_HUND),
  );
  yield takeEvery(
    ThhHundActions.ABSPRUNG_INS_DASHBOARD,
    safeSaga(generateProduktData, App.THH_HUND),
  );
}

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  const produktActionData: ThhHundProduktPayload = {
    appId: App.THH_HUND,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: THH_HUND_UEBERSICHT_TITLE,
    versichertesRisiko: getVersichertesRisiko(
      state.versicherungsnehmer.anzahlTiere,
      state.versicherungsnehmer.anzahlMehrereHunde,
    ),
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    ausgewaehlteHunderassen: getAusgewaehlteHundeRassen(state.versicherungsnehmer),
    ausgewaehlteHunderassenValue: getAusgewaehlteHundeRassenValue(state.versicherungsnehmer),
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    beginndatum: state.vertragsoptionen.beginnDatum,
    tiere:
      state.versicherungsnehmer.tiere?.length > 0
        ? getTiere(
            state.versicherungsnehmer.tiere,
            getAusgewaehlteHundeRassenValue(state.versicherungsnehmer),
          )
        : [],
    isWeitererVertrag: state.vertragsoptionen.isWeitererVertrag,
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    ...(state.versicherungsnehmer.tiergruppeAnimals
      ? { tiergruppeAnimals: state.versicherungsnehmer.tiergruppeAnimals }
      : {}),
  };
  yield call(globalPut.put, new GlobalActions().produktAction(produktActionData));
}

function getTiere(tiere: ThhTier[], hunderassen: Array<string>): ThhTier[] {
  const newList: ThhTier[] = [];
  const tierTicker = tiere.map(s => s);
  hunderassen.forEach(value => {
    const findElement = tierTicker.find(value1 => value1.tierrasse === value);
    if (findElement && tierTicker.includes(findElement)) {
      tierTicker.splice(tierTicker.indexOf(findElement), 1);
      newList.push({
        instanzId: findElement.instanzId,
        tierrasse: findElement.tierrasse,
        geburtsdatumTier: findElement.geburtsdatumTier,
        geschlecht: findElement.geschlecht,
        identifizierungsart: findElement.identifizierungsart,
        identifizierungscode: findElement.identifizierungscode,
        identifizierungsstelle: findElement.identifizierungsstelle,
        tierart: findElement.tierart,
        tiername: findElement.tiername,
      });
    } else {
      newList.push({
        geburtsdatumTier: '',
        geschlecht: '',
        identifizierungsart: '',
        identifizierungscode: '',
        identifizierungsstelle: '',
        instanzId: 0,
        tierart: undefined,
        tiername: '',
        tierrasse: value,
      });
    }
  });
  return newList;
}

function getAusgewaehlteHundeRassenValue(versicherungsnehmer: Versicherungsnehmer): Array<string> {
  const anzahlTiereEnumToAnzahlMapper = {
    [AnzahlTiereEnum.EINES]: [versicherungsnehmer.rasseEinHund],
    [AnzahlTiereEnum.MEHRERE]: versicherungsnehmer.rasseMehrereHunde,
  };
  return anzahlTiereEnumToAnzahlMapper[versicherungsnehmer.anzahlTiere];
}

function getAusgewaehlteHundeRassen(versicherungsnehmer: Versicherungsnehmer): Array<string> {
  const hunderassenAuswahlElemente = versicherungsnehmer.hunderassenAuswahlElemente;
  return getAusgewaehlteHundeRassenValue(versicherungsnehmer).map(element =>
    getHunderasseDescriptionForValue(element, hunderassenAuswahlElemente),
  );
}

function getHunderasseDescriptionForValue(
  hunderassValue: string,
  hunderassenAuswahlElemente: Array<ValueLabelElement>,
): string {
  return hunderassenAuswahlElemente.find(
    hunderassenElement => hunderassenElement.value === hunderassValue,
  )?.label;
}

export function* generateDashboardData(action?: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new ThhHundActions().vorschlagdatenChanged(action?.payload?.deferTarifierung === true));

  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().inDashboardUebernehmen({
      appId: App.THH_HUND,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versichertesRisiko: getVersichertesRisiko(
        state.versicherungsnehmer.anzahlTiere,
        state.versicherungsnehmer.anzahlMehrereHunde,
      ),
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      beginn: state.vertragsoptionen.beginnDatum,
      ...(action?.payload?.deferTarifierung ? { deferTarifierung: true } : {}),
    }),
  );
}

function getVersichertesRisiko(mehrereTiere: AnzahlTiereEnum, anzahl: number): string {
  if (mehrereTiere === AnzahlTiereEnum.EINES) {
    return contentData['versichertesRisiko']['einHund'];
  } else if (mehrereTiere === AnzahlTiereEnum.MEHRERE) {
    return contentData['versichertesRisiko']['mehrereHunde'] + ` (${anzahl})`;
  }
  return '';
}
